import React, { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { authV9 as auth } from "../firebase";
import "./FirebaseAuth.css";
import SpotLogoLogin from "../img/logo-temp-cover.png";

function FirebaseAuth() {
  const [isLogin, setIsLogin] = useState(true); // Toggle between login and register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  // Load CookieYes banner script
  useEffect(() => {
    const script = document.createElement('script');
    script.id = 'cookieyes';
    script.type = 'text/javascript';
    script.src = 'https://cdn-cookieyes.com/client_data/0886897084eed81062a37c5d/script.js';
    script.async = true;

    // Add script to document head
    document.head.appendChild(script);

    // Cleanup function to remove script when component unmounts
    return () => {
      const existingScript = document.getElementById('cookieyes');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    try {
      if (isLogin) {
        // Login
        await signInWithEmailAndPassword(auth, email, password);
        // User will be redirected by App.js auth state listener
      } else {
        // Register
        if (password !== confirmPassword) {
          setError("Passwords don't match");
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          setError("Password must be at least 6 characters");
          setLoading(false);
          return;
        }
        await createUserWithEmailAndPassword(auth, email, password);
        // User will be redirected by App.js auth state listener
      }
    } catch (err) {
      console.error("Auth error:", err);
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError("This email is already registered. Please login instead.");
          break;
        case 'auth/invalid-email':
          setError("Invalid email address");
          break;
        case 'auth/user-not-found':
          setError("No account found with this email");
          break;
        case 'auth/wrong-password':
          setError("Incorrect password");
          break;
        case 'auth/weak-password':
          setError("Password is too weak");
          break;
        case 'auth/too-many-requests':
          setError("Too many failed attempts. Please try again later.");
          break;
        default:
          setError("Authentication failed. Please try again.");
      }
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    if (!email) {
      setError("Please enter your email address");
      setLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset email sent! Check your inbox.");
      setShowResetPassword(false);
      setTimeout(() => {
        setMessage("");
      }, 5000);
    } catch (err) {
      console.error("Password reset error:", err);
      switch (err.code) {
        case 'auth/user-not-found':
          setError("No account found with this email");
          break;
        case 'auth/invalid-email':
          setError("Invalid email address");
          break;
        default:
          setError("Failed to send reset email. Please try again.");
      }
    }
    setLoading(false);
  };

  return (
    <div className="auth-landing-container">
      <div className="auth-landing-content">
        <img
          src={SpotLogoLogin}
          alt="Song Carousel"
          className="login-logo"
        />

        <h1 className="auth-landing-main-headline">
          Do you want to get real human streams? Then Song Carousel is for you.
        </h1>

        <p className="auth-landing-intro-text">
          Use Song Carousel to get real human plays and feedback on your Spotify songs.
          Listen and rate songs of others and then add your song.
          It's free to use, check the <a href="/faq" className="landing-faq-link">FAQ</a> if you
          have more questions.
        </p>

        {/* Auth Form */}
        {!showResetPassword ? (
          <div className="auth-form-section">
            <div className="auth-toggle">
              <div className="auth-toggle-switch">
                <div className={`auth-toggle-slider ${isLogin ? 'left' : 'right'}`}></div>
                <button
                  className={`auth-toggle-option ${isLogin ? 'active' : ''}`}
                  onClick={() => {
                    setIsLogin(true);
                    setError("");
                    setMessage("");
                  }}
                  type="button"
                >
                  Login
                </button>
                <button
                  className={`auth-toggle-option ${!isLogin ? 'active' : ''}`}
                  onClick={() => {
                    setIsLogin(false);
                    setError("");
                    setMessage("");
                  }}
                  type="button"
                >
                  Register
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {error && <div className="auth-error">{error}</div>}
              {message && <div className="auth-success">{message}</div>}

              <div className="auth-input-group">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                  disabled={loading}
                  className="auth-input"
                />
              </div>

              <div className="auth-input-group">
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  disabled={loading}
                  minLength={6}
                  className="auth-input"
                />
              </div>

              {!isLogin && (
                <div className="auth-input-group">
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm Password"
                    required
                    disabled={loading}
                    minLength={6}
                    className="auth-input"
                  />
                </div>
              )}

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? "Loading..." : (isLogin ? "Login" : "Create Account")}
              </button>

              {isLogin && (
                <button
                  type="button"
                  className="auth-forgot-password"
                  onClick={() => {
                    setShowResetPassword(true);
                    setError("");
                    setMessage("");
                  }}
                  disabled={loading}
                >
                  Forgot Password?
                </button>
              )}
            </form>
          </div>
        ) : (
          <div className="auth-form-section">
            <h2 className="auth-reset-title">Reset Password</h2>
            <form onSubmit={handlePasswordReset} className="auth-form">
              {error && <div className="auth-error">{error}</div>}
              {message && <div className="auth-success">{message}</div>}

              <div className="auth-input-group">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                  disabled={loading}
                  className="auth-input"
                />
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Email"}
              </button>

              <button
                type="button"
                className="auth-back-btn"
                onClick={() => {
                  setShowResetPassword(false);
                  setError("");
                  setMessage("");
                }}
                disabled={loading}
              >
                Back to Login
              </button>
            </form>
          </div>
        )}

        <p className="auth-landing-security-text">




        <br/> <br/>

      Your account is secure and protected by industry-standard encryption.
        </p>
      </div>
    </div>
  );
}

export default FirebaseAuth;
